/**
 * Fluxo de Caixa Diário — Liquidação e Conciliação
 * (prototype: erpone_fluxo_de_liquida_o_e_concilia_o)
 *
 * Contrato: GET /finance/flow/daily?days=7 → DailyFlowDto
 *   { days: [{ date, inflow, outflow, net, items: [{ id, date, type, side,
 *       description, bankAccount, accountCode, accountingReference, value }] }],
 *     totalInflow, totalOutflow, net }
 *
 * A tela do protótipo mostra coluna "Status" (Conciliado/Pendente), que o
 * backend NÃO publica por item: o que o servidor manda é o LADO da transação
 * (`side: INFLOW|OUTFLOW`). A coluna renderiza esse lado como badge
 * (Entrada/Saída) — derivação rotulada, não campo inventado.
 *
 * Gráfico "Entradas vs Saídas (7 dias)": barras empilhadas client-side a
 * partir de `days[].inflow/outflow`. Nenhuma outra chamada é feita — os
 * filtros do protótipo (banco, centro de custo) não têm parâmetro no contrato.
 */
import { useMemo } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  LoadingSkeleton,
  Button,
} from '@nexora/design-system';
import { useDailyFlow } from './treasuryHooks';
import { FLOW_SIDE_LABEL, type DailyFlowItem, type FlowSide } from './treasuryTypes';
import { formatCurrency, formatDate } from '../../../shared/utils/format';

/* Status do protótipo derivado do lado da transação — o contrato não tem
   "status" por item. */

const SIDE_META: Record<FlowSide, { variant: 'success' | 'error' | 'info'; label: string }> = {
  INFLOW: { variant: 'success', label: 'Entrada' },
  OUTFLOW: { variant: 'error', label: 'Saída' },
};

const MONEY_CELL = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontVariantNumeric: 'tabular-nums' as const,
  textAlign: 'right' as const,
};

interface Row extends DailyFlowItem {
  dayDate: string;
}

export function DailyCashFlowPage(): JSX.Element {
  const { data: flow, isLoading, error, refetch } = useDailyFlow(7);

  /* Achata dias → linhas da tabela; a data do item que o DTO manda é a do dia. */
  const rows: Row[] = useMemo(() => {
    return (flow?.days ?? []).flatMap((day) =>
      (day.items ?? []).map((item) => ({ ...item, dayDate: day.date })),
    );
  }, [flow]);

  const days = useMemo(() => flow?.days ?? [], [flow]);
  const totalInflow = flow?.totalInflow ?? 0;
  const totalOutflow = flow?.totalOutflow ?? 0;
  const net = flow?.net ?? 0;

  const maxDayValue = useMemo(
    () => Math.max(1, ...days.map((d) => Math.max(d.inflow, d.outflow))),
    [days],
  );

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Fluxo de Caixa Diário</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Liquidação e movimentos processados — últimos 7 dias.
          </p>
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="2rem">
          <LoadingSkeleton count={4} height="48px" />
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Carregando fluxo de caixa...</p>
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem" className="text-centered">
          <p style={{ color: '#ba1a1a', fontWeight: 600, margin: 0 }}>Erro ao carregar o fluxo de caixa</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f' }}>{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      )}

      {/* ── Empty honesto ── */}
      {!isLoading && !error && days.length === 0 && (
        <Card padding="2rem" className="text-centered muted">
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            Nenhum movimento processado nos últimos 7 dias.
          </p>
        </Card>
      )}

      {/* ── Conteúdo ── */}
      {!isLoading && !error && days.length > 0 && (
        <>
          {/* Metric cards: Total Recebido / Total Pago / Saldo Líquido */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mb-lg">
            <div className="bg-surface-container-lowest rounded-xl p-md border border-outline-variant/50 shadow-sm">
              <div className="flex items-center gap-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider mb-sm">
                <span className="material-symbols-outlined text-sm text-secondary">arrow_downward</span>
                Total Recebido
              </div>
              <div className="font-headline-md text-headline-md text-on-surface tabular-nums">
                {formatCurrency(totalInflow)}
              </div>
              <div className="mt-md text-body-sm text-body-sm text-on-surface-variant">
                {days.length} {days.length === 1 ? 'dia' : 'dias'} no período
              </div>
            </div>
            <div className="bg-surface-container-lowest rounded-xl p-md border border-outline-variant/50 shadow-sm">
              <div className="flex items-center gap-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider mb-sm">
                <span className="material-symbols-outlined text-sm text-error">arrow_upward</span>
                Total Pago
              </div>
              <div className="font-headline-md text-headline-md text-on-surface tabular-nums">
                {formatCurrency(totalOutflow)}
              </div>
              <div className="mt-md text-body-sm text-body-sm text-on-surface-variant">
                {rows.length} {rows.length === 1 ? 'movimento' : 'movimentos'} processados
              </div>
            </div>
            <div className="bg-primary text-on-primary rounded-xl p-md shadow-sm">
              <div className="flex items-center gap-xs text-on-primary/80 font-label-md text-label-md uppercase tracking-wider mb-sm">
                <span className="material-symbols-outlined text-sm">account_balance</span>
                Saldo Líquido
              </div>
              <div className="font-headline-md text-headline-md text-on-primary tabular-nums">
                {formatCurrency(net)}
              </div>
              <div className="mt-md text-body-sm text-body-sm text-on-primary/90">
                Resultado líquido do período
              </div>
            </div>
          </div>

          {/* Chart: Entradas vs Saídas (7 dias) */}
          <Card variant="outlined" padding="1rem" className="mb-lg">
            <CardHeader>
              <h3 className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider m-0">
                Entradas vs Saídas (7 dias)
              </h3>
            </CardHeader>
            <div className="flex flex-1 items-end justify-between gap-xs pt-md h-40">
              {days.map((d) => {
                const hIn = (d.inflow / maxDayValue) * 100;
                const hOut = (d.outflow / maxDayValue) * 100;
                return (
                  <div key={d.date} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                    <div className="flex flex-col justify-end w-full gap-1" style={{ height: '100%' }}>
                      <div
                        className="bg-primary-container w-full rounded-t-sm hover:bg-primary transition-colors"
                        style={{ height: `${Math.max(2, hIn)}%` }}
                        title={`${formatDate(d.date)} · Entradas ${formatCurrency(d.inflow)}`}
                      />
                      <div
                        className="bg-surface-variant w-full rounded-t-sm"
                        style={{ height: `${Math.max(2, hOut)}%` }}
                        title={`${formatDate(d.date)} · Saídas ${formatCurrency(d.outflow)}`}
                      />
                    </div>
                    <span className="text-[10px] text-on-surface-variant font-label-sm whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Tabela de movimentos processados */}
          <Card variant="outlined" padding="0" className="overflow-hidden">
            <CardHeader className="px-md pt-md" action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {rows.length} {rows.length === 1 ? 'movimento' : 'movimentos'}
              </span>
            }>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Movimentos Processados</h3>
            </CardHeader>
            <div className="tr-table-wrap">
              <table className="tr-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Descrição</th>
                    <th>Banco</th>
                    <th>Ref. Contábil</th>
                    <th className="text-right">Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Badge variant={SIDE_META[r.side]?.variant ?? 'info'} size="sm">
                          {FLOW_SIDE_LABEL[r.side] ?? r.side}
                        </Badge>
                      </td>
                      <td>{formatDate(r.dayDate)}</td>
                      <td>{r.type}</td>
                      <td className="tr-cell-strong">{r.description}</td>
                      <td className="tr-cell-muted">{r.bankAccount || '—'}</td>
                      <td className="tr-cell-mono">{r.accountingReference ?? '—'}</td>
                      <td className="tr-cell-money" style={MONEY_CELL}>
                        {formatCurrency(r.value)}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="tr-cell-muted tr-cell-center">
                        Nenhum movimento nos dias retornados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <style>{`
        .text-centered { text-align: center; }
        .muted { color: #74777f; }
        .tr-table-wrap { overflow-x: auto; }
        .tr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .tr-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase;
          letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5;
          text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .tr-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .tr-table tbody tr:hover td { background: #eceef0; }
        .tr-cell-strong { font-weight: 600; }
        .tr-cell-muted { color: #43474e; }
        .tr-cell-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.8125rem; color: #43474e; }
        .tr-cell-money { font-weight: 600; color: #002045; }
        .tr-cell-center { text-align: center; }
      `}</style>
    </div>
  );
}